import http from 'hub-http/clients/apiClient';
import Article, { oldKeys } from '../records/ArticleRecord';
import { makeSimpleQuickFetchWrapper } from '../utils/ajaxUtils';
import { addSCPArticlesToMigrationCache, getPrimaryKBIdForArticleId, getShouldCallArticleACPEndpoints, getShouldCallSCPEndpoints, getTranslatedKBIdForTranslatedFromArticleId } from '../migration-handler/singleton/migrationHandler';
import { getLegacyArticlesQuery } from '../utils/legacyArticleQueryUtils';
import { getArticlesQuery } from '../utils/articleQueryUtils';
import pick from 'transmute/pick';
import { FILTERS_FOR_ARTICLES_COUNT, FILTERS_FOR_BULK_OPERATIONS } from '../constants/manage/ArticleQueryConstants';
import { LEGACY_FILTERS_FOR_ARTICLES_COUNT, LEGACY_FILTERS_FOR_BULK_OPERATIONS } from '../constants/manage/LegacyArticleQueryConstants';
import { Map as ImmutableMap } from 'immutable';
const API_BASE = '/knowledge-content/v1/knowledge-articles';
const CATEGORY_API_BASE = '/knowledge-content/v1/knowledge-category-organization';
const CATEGORY_API_BASE_V2 = 'knowledge-content/v2/category-organization/buffer/knowledge-base';
const SCP_API_BASE = '/knowledge-content/v2/knowledge-article';
const SCP_DASHBOARD_API_BASE = '/knowledge-content/v2/dashboard/knowledge-base';

// to include only needed properties for OldArticleRecord
const ALLOWED_ARTICLE_RECORD_PROPERTIES = oldKeys;
const queryToEarlyRequestSuffix = query => {
  const {
    archived,
    state
  } = query;
  if (archived && !state) {
    return 'ARCHIVED';
  } else if (!archived && !state) {
    return 'ALL';
  } else if (!archived && state === 'DRAFT') {
    return 'DRAFT';
  } else if (!archived && state === 'PUBLISHED') {
    return 'PUBLISHED';
  }
  return '';
};
export default class ArticlesApi {
  static getNonVariationArticlesFilteredByLanguage(query, knowledgeBaseId) {
    const isOnSCP = getShouldCallSCPEndpoints(knowledgeBaseId);
    if (!isOnSCP) {
      return http.get(`${API_BASE}/language-masters`, {
        query: {
          property: ['id', 'name', 'language'],
          name__icontains: query.hs_name__icontains,
          language__in: query.hs_language__in,
          offset: atob(query.after)
        }
      });
    }
    return http.get(`${SCP_API_BASE}`, {
      query
    }).then(response => {
      return {
        objects: response.results,
        offset: query.offset,
        total: response.total
      };
    });
  }
  static create(articleData) {
    const isOnSCP = articleData.values && getShouldCallSCPEndpoints(articleData.values.hs_group_id);
    if (!isOnSCP) {
      return http.post(API_BASE, {
        data: Article.toOldFormat(articleData)
      });
    }
    return http.post(SCP_API_BASE, {
      data: articleData
    });
  }
  static setOrdering(parentCategoryId, orderedChildIds) {
    return http.put(`${API_BASE}/order/${parentCategoryId}`, {
      data: orderedChildIds,
      // Increasing the default timeout by 3 times
      // 14000 => 14000 * 3 = 42000
      timeout: 42000
    });
  }
  static setOrderingBuffer(parentCategoryId, orderedChildIds, language, knowledgeBaseId) {
    const isOnSCP = getShouldCallSCPEndpoints(knowledgeBaseId);
    if (!isOnSCP) {
      return http.put(`${API_BASE}/order/${parentCategoryId}/buffer`, {
        data: orderedChildIds,
        query: {
          language,
          knowledgeBaseId
        }
      });
    }
    return http.put(`${CATEGORY_API_BASE_V2}/${knowledgeBaseId}/order-articles`, {
      data: {
        parentId: parentCategoryId,
        ids: orderedChildIds
      }
    });
  }
  static moveArticleToCategory(parentCategoryId, articleIds, knowledgeBaseId) {
    const isOnSCP = getShouldCallSCPEndpoints(knowledgeBaseId);
    if (!isOnSCP) {
      return http.put(`${CATEGORY_API_BASE}/knowledge-base/${knowledgeBaseId}/buffer/category/${parentCategoryId}/add`, {
        data: {
          articleIds
        },
        query: {
          knowledgeBaseId
        }
      });
    }
    return http.post(`${CATEGORY_API_BASE_V2}/${knowledgeBaseId}/add-articles`, {
      data: {
        parentId: Number(parentCategoryId),
        ids: articleIds
      }
    });
  }
  static updateArticle(articleId, data, lastEditTicks, sessionId, sessionUpdateId) {
    const shouldCallSCPEndpoint = getShouldCallArticleACPEndpoints(articleId);
    if (!shouldCallSCPEndpoint) {
      return http.putWithResponse(`${API_BASE}/${articleId}`, {
        data: Article.toOldFormat(data),
        query: {
          ifUnmodifiedSinceTicks: lastEditTicks,
          editorSessionId: sessionId,
          editorUpdateId: sessionUpdateId
        }
      });
    }
    return http.patchWithResponse(`${SCP_API_BASE}/${articleId}`, {
      data
    });
  }
  static updateArticleBuffer(articleId, data, lastEditTicks, sessionId, sessionUpdateId) {
    return http.putWithResponse(`${API_BASE}/${articleId}/buffer`, {
      data: Article.toOldFormat(data),
      query: {
        ifUnmodifiedSinceTicks: lastEditTicks,
        editorSessionId: sessionId,
        editorUpdateId: sessionUpdateId
      }
    });
  }
  static delete(articleId) {
    const shouldCallSCPEndpoint = getShouldCallArticleACPEndpoints(articleId);
    const deleteArticleURL = `${shouldCallSCPEndpoint ? SCP_API_BASE : API_BASE}/${articleId}`;
    return http.delete(deleteArticleURL);
  }
  static bulkDelete(articleIds) {
    const shouldCallSCPEndpoint = getShouldCallArticleACPEndpoints(articleIds[0]);
    if (!shouldCallSCPEndpoint) {
      const bulkDeleteURL = `${API_BASE}/bulk`;
      const query = {
        id: articleIds,
        failIfNotAllSucceed: true
      };
      return http.delete(bulkDeleteURL, {
        query
      });
    }
    const knowledgeBaseId = getPrimaryKBIdForArticleId(articleIds[0]);
    return http.delete(`${SCP_DASHBOARD_API_BASE}/${knowledgeBaseId}/knowledge-article/filtered-bulk`, {
      query: {
        hs_id__in: articleIds
      },
      timeout: 30000
    });
  }
  static archive(articleId) {
    const shouldCallSCPEndpoint = getShouldCallArticleACPEndpoints(articleId);
    if (!shouldCallSCPEndpoint) {
      return http.put(`${API_BASE}/${articleId}`, {
        data: {
          archived: true
        }
      });
    }
    return http.patch(`${SCP_API_BASE}/${articleId}`, {
      data: {
        values: {
          hs_archived_in_dashboard: 1
        }
      }
    });
  }
  static unarchive(articleId) {
    const shouldCallSCPEndpoint = getShouldCallArticleACPEndpoints(articleId);
    if (!shouldCallSCPEndpoint) {
      return http.put(`${API_BASE}/${articleId}`, {
        data: {
          archived: false
        }
      });
    }
    return http.patch(`${SCP_API_BASE}/${articleId}`, {
      data: {
        values: {
          hs_archived_in_dashboard: 0
        }
      }
    });
  }
  static bulkArchive(articleIds) {
    const shouldCallSCPEndpoint = getShouldCallArticleACPEndpoints(articleIds[0]);
    if (!shouldCallSCPEndpoint) {
      const bulkArchiveArticleURL = `${API_BASE}/archive`;
      const query = {
        id: articleIds,
        failIfNotAllSucceed: true
      };
      const data = {
        isArchived: true
      };
      return http.put(bulkArchiveArticleURL, {
        query,
        data
      });
    }
    const knowledgeBaseId = getPrimaryKBIdForArticleId(articleIds[0]);
    return http.patch(`${SCP_DASHBOARD_API_BASE}/${knowledgeBaseId}/knowledge-article/filtered-bulk/archival-in-dashboard`, {
      query: {
        hs_id__in: articleIds
      },
      data: {
        hs_archived_in_dashboard: 1
      },
      timeout: 30000
    });
  }
  static bulkUnarchive(articleIds) {
    const shouldCallSCPEndpoint = getShouldCallArticleACPEndpoints(articleIds[0]);
    if (!shouldCallSCPEndpoint) {
      const bulkArchiveArticleURL = `${API_BASE}/archive`;
      const query = {
        id: articleIds,
        failIfNotAllSucceed: true
      };
      const data = {
        isArchived: false
      };
      return http.put(bulkArchiveArticleURL, {
        query,
        data
      });
    }
    const knowledgeBaseId = getPrimaryKBIdForArticleId(articleIds[0]);
    return http.patch(`${SCP_DASHBOARD_API_BASE}/${knowledgeBaseId}/knowledge-article/filtered-bulk/archival-in-dashboard`, {
      query: {
        hs_id__in: articleIds
      },
      data: {
        hs_archived_in_dashboard: 0
      },
      timeout: 30000
    });
  }
  static bulkArchiveAndUnpublish(articleIds) {
    const shouldCallSCPEndpoint = getShouldCallArticleACPEndpoints(articleIds[0]);
    if (!shouldCallSCPEndpoint) {
      const archiveAndUnpublishArticleURL = `${API_BASE}/archive-and-unpublish`;
      const query = {
        id: articleIds,
        failIfNotAllSucceed: true
      };
      return http.put(archiveAndUnpublishArticleURL, {
        query
      });
    }
    const knowledgeBaseId = getPrimaryKBIdForArticleId(articleIds[0]);
    return http.patch(`${SCP_DASHBOARD_API_BASE}/${knowledgeBaseId}/knowledge-article/filtered-bulk/archive-in-dashboard-and-unpublish`, {
      query: {
        hs_id__in: articleIds
      },
      timeout: 30000
    });
  }
  static publish(articleId) {
    const shouldCallSCPEndpoint = getShouldCallArticleACPEndpoints(articleId);
    if (!shouldCallSCPEndpoint) {
      const publishURL = `${API_BASE}/${articleId}/publish-action`;
      const data = {
        action: 'schedule-publish'
      };
      return http.post(publishURL, {
        data
      });
    }
    return http.post(`${SCP_API_BASE}/${articleId}/draft/push-live`);
  }
  static unpublish(articleId, isOnLegacyKB) {
    if (isOnLegacyKB) {
      const unPublishURL = `${API_BASE}/${articleId}/publish-action`;
      const data = {
        action: 'cancel-publish'
      };
      return http.post(unPublishURL, {
        data
      });
    }
    return http.post(`${SCP_API_BASE}/${articleId}/unpublish`);
  }
  static getAuthors(knowledgeBaseId) {
    const isOnSCP = getShouldCallSCPEndpoints(knowledgeBaseId);
    if (!isOnSCP) {
      return http.get(`${API_BASE}/authors`, {
        query: {
          knowledgeBaseId
        }
      });
    }
    return http.get(`${SCP_DASHBOARD_API_BASE}/${knowledgeBaseId}/article-authors`).then(({
      results
    }) => results);
  }
  static filteredBulkArchive(manageFilters, state, knowledgeBaseId) {
    const isOnSCP = getShouldCallSCPEndpoints(knowledgeBaseId);
    if (!isOnSCP) {
      const bulkArchiveArticleURL = `${API_BASE}/filtered-bulk-archive-and-unpublish`;
      const data = {
        isArchived: true
      };
      return http.put(bulkArchiveArticleURL, {
        data,
        query: ImmutableMap(pick(LEGACY_FILTERS_FOR_BULK_OPERATIONS, getLegacyArticlesQuery(manageFilters, state, knowledgeBaseId))).filter(field => field != null).toJS(),
        timeout: 30000
      });
    }
    return http.patch(`${SCP_DASHBOARD_API_BASE}/${knowledgeBaseId}/knowledge-article/filtered-bulk/archive-in-dashboard-and-unpublish`, {
      query: ImmutableMap(pick(FILTERS_FOR_BULK_OPERATIONS, getArticlesQuery(manageFilters, state, knowledgeBaseId))).filter(field => field != null).toJS(),
      timeout: 30000
    });
  }
  static filteredBulkDelete(manageFilters, state, knowledgeBaseId) {
    const isOnSCP = getShouldCallSCPEndpoints(knowledgeBaseId);
    if (!isOnSCP) {
      const bulkDeleteArticleURL = `${API_BASE}/filtered-bulk`;
      return http.delete(bulkDeleteArticleURL, {
        query: ImmutableMap(pick(LEGACY_FILTERS_FOR_BULK_OPERATIONS, getLegacyArticlesQuery(manageFilters, state, knowledgeBaseId))).filter(field => field != null).toJS(),
        timeout: 30000
      });
    }
    return http.delete(`${SCP_DASHBOARD_API_BASE}/${knowledgeBaseId}/knowledge-article/filtered-bulk`, {
      query: ImmutableMap(pick(FILTERS_FOR_BULK_OPERATIONS, getArticlesQuery(manageFilters, state, knowledgeBaseId))).filter(field => field != null).toJS(),
      timeout: 30000
    });
  }
  static filteredBulkUnarchive(manageFilters, state, knowledgeBaseId) {
    const isOnSCP = getShouldCallSCPEndpoints(knowledgeBaseId);
    if (!isOnSCP) {
      const bulkDeleteArticleURL = `${API_BASE}/filtered-bulk-archive`;
      const data = {
        isArchived: false
      };
      return http.put(bulkDeleteArticleURL, {
        query: ImmutableMap(pick(LEGACY_FILTERS_FOR_BULK_OPERATIONS, getLegacyArticlesQuery(manageFilters, state, knowledgeBaseId))).filter(field => field != null).toJS(),
        timeout: 30000,
        data
      });
    }
    return http.patch(`${SCP_DASHBOARD_API_BASE}/${knowledgeBaseId}/knowledge-article/filtered-bulk/archival-in-dashboard`, {
      query: ImmutableMap(pick(FILTERS_FOR_BULK_OPERATIONS, getArticlesQuery(manageFilters, state, knowledgeBaseId))).filter(field => field != null).toJS(),
      data: {
        hs_archived_in_dashboard: 0
      },
      timeout: 30000
    });
  }
  static createLanguageVariant(masterArticleId, name, language) {
    const isOnSCP = getShouldCallArticleACPEndpoints(masterArticleId);
    if (!isOnSCP) {
      const url = `${API_BASE}/${masterArticleId}/create-language-variation`;
      const data = {
        language,
        name
      };
      return http.post(url, {
        data
      });
    }
    const translatedKBId = getTranslatedKBIdForTranslatedFromArticleId(masterArticleId, language);
    return http.post(SCP_API_BASE, {
      data: {
        values: {
          hs_translated_from_id: masterArticleId,
          hs_name: name,
          hs_group_id: translatedKBId
        }
      }
    });
  }
  static detachFromLanguageGroup(articleId) {
    if (!getShouldCallArticleACPEndpoints(articleId)) {
      return http.putWithResponse(`${API_BASE}/${articleId}/detach-from-lang-group`);
    }
    return http.patch(`${SCP_API_BASE}/${articleId}`, {
      data: {
        values: {
          hs_translated_from_id: ''
        }
      }
    });
  }
  static attachToLangMaster(article, masterArticleId) {
    const isOnSCP = getShouldCallArticleACPEndpoints(article.values.hs_id);
    if (!isOnSCP) {
      const url = `${API_BASE}/${article.values.hs_id}/attach-to-lang-master`;
      const data = {
        translatedFromId: masterArticleId,
        language: article.values.hs_language
      };
      return http.putWithResponse(url, {
        data
      });
    }
    const url = `${SCP_API_BASE}/${article.values.hs_id}`;
    const data = {
      values: {
        hs_translated_from_id: masterArticleId
      }
    };
    return http.patchWithResponse(url, {
      data
    });
  }
  static fetchVersions(articleId, offset = 0) {
    const url = `${API_BASE}/${articleId}/versions-counted`;
    return http.get(url, {
      query: {
        offset,
        limit: 10
      }
    });
  }
  static restoreVersion(articleId, versionId) {
    const url = `${API_BASE}/${articleId}/versions/restore-to-buffer`;
    return http.post(url, {
      data: {
        versionId
      }
    });
  }
  static clone(articleId, cloneArticleName) {
    return http.post(`knowledge-content/v2/knowledge-article/${articleId}/clone`, {
      data: {
        hs_name: cloneArticleName
      }
    });
  }
}
ArticlesApi.get = makeSimpleQuickFetchWrapper(({
  filterParams,
  articleState,
  knowledgeBaseId
}) => {
  const isOnSCP = getShouldCallSCPEndpoints(knowledgeBaseId);
  if (!isOnSCP) {
    return `get-article-listing-${queryToEarlyRequestSuffix(getLegacyArticlesQuery(filterParams, articleState, knowledgeBaseId).toJS())}`;
  }
  return `get-article-listing-${queryToEarlyRequestSuffix(getArticlesQuery(filterParams, articleState, knowledgeBaseId).toJS())}`;
}, ({
  filterParams,
  articleState,
  knowledgeBaseId
}) => {
  const isOnSCP = getShouldCallSCPEndpoints(knowledgeBaseId);
  if (!isOnSCP) {
    return http.get(API_BASE, {
      query: Object.assign({
        property: ALLOWED_ARTICLE_RECORD_PROPERTIES
      }, getLegacyArticlesQuery(filterParams, articleState, knowledgeBaseId).toJS())
    });
  }
  return http.get(`${SCP_DASHBOARD_API_BASE}/${knowledgeBaseId}/knowledge-article`, {
    query: getArticlesQuery(filterParams, articleState, knowledgeBaseId).remove('hs_group_id').toJS()
  }).then(response => {
    addSCPArticlesToMigrationCache(response.results);
    return {
      objects: response.results,
      offset: filterParams.offset,
      total: response.total,
      totalCount: response.total,
      limit: filterParams.limit
    };
  });
});
ArticlesApi.getIndividualArticle = makeSimpleQuickFetchWrapper(articleId => `get-individual-article-${articleId}`, articleId => {
  const shouldCallSCPEndpoint = getShouldCallArticleACPEndpoints(articleId);
  const requestSCPArticles = () => http.get(SCP_API_BASE, {
    query: {
      hs_id__eq: articleId
    }
  }).then(response => {
    addSCPArticlesToMigrationCache(response.results);
    return {
      objects: response.results,
      total: response.total,
      totalCount: response.total
    };
  });
  if (!shouldCallSCPEndpoint) {
    return http.get(API_BASE, {
      query: {
        id__eq: articleId,
        property: ALLOWED_ARTICLE_RECORD_PROPERTIES
      }
    })
    //@ts-expect-error some weird typing issue
    .then(response => {
      return response.objects.length === 0 ? requestSCPArticles() : response;
    });
  }
  return requestSCPArticles();
});
ArticlesApi.getArticleHasBufferedChanges = makeSimpleQuickFetchWrapper(articleId => `get-article-has-buffered-changes-${articleId}`, articleId => http.get(`${API_BASE}/${articleId}/has-buffered-changes`));
ArticlesApi.getCounts = makeSimpleQuickFetchWrapper('get-articles-count', (manageFilters, knowledgeBaseId) => {
  const isOnSCP = getShouldCallSCPEndpoints(knowledgeBaseId);
  if (!isOnSCP) {
    return http.get(`${API_BASE}/count`, {
      query: ImmutableMap(pick(LEGACY_FILTERS_FOR_ARTICLES_COUNT, getLegacyArticlesQuery(manageFilters, undefined, knowledgeBaseId))).filter(field => field != null).toJS()
    });
  }
  return http.get(`${SCP_DASHBOARD_API_BASE}/${knowledgeBaseId}/article-count`, {
    query: ImmutableMap(pick(FILTERS_FOR_ARTICLES_COUNT, getArticlesQuery(manageFilters, undefined, knowledgeBaseId))).filter(field => field != null).toJS()
  });
});